import {
  Directive,
  ElementRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

export interface InjectAttributeModel {
  CSSPath: string;
  attribute: string;
  value: string;
}

@Directive({
  selector: '[injectAttribute]',
  standalone: true,
})
export class InjectAttributeDirective {
  /**
   * `injectAttribute` does not support shrinking the array of values.
   * The size of the provided array must stay the same or can grow.
   */
  injectAttribute = input.required<InjectAttributeModel[]>();

  private previousValue = signal<InjectAttributeModel[]>([]);

  private valuesChanged = computed(() => {
    interface Change {
      CSSPath: boolean;
      attribute: boolean;
      value: boolean;
    }

    const changes: Change[] = [];

    if (
      this.injectAttribute() === undefined ||
      this.injectAttribute().length === 0
    ) {
      return changes;
    }

    this.injectAttribute().forEach((currentValue, i) => {
      const change: Change = {
        CSSPath: false,
        attribute: false,
        value: false,
      };

      if (
        this.injectAttribute() !== undefined &&
        (this.previousValue() === undefined ||
          this.previousValue().length === 0)
      ) {
        changes.push({
          CSSPath: true,
          attribute: true,
          value: true,
        });

        return;
      }

      if (currentValue.CSSPath !== this.previousValue()[i].CSSPath) {
        change.CSSPath = true;
      }

      if (currentValue.attribute !== this.previousValue()[i].attribute) {
        change.attribute = true;
      }

      if (currentValue.value !== this.previousValue()[i].value) {
        change.value = true;
      }

      changes.push(change);
    });

    return changes;
  });

  private el: ElementRef<HTMLElement> = inject(ElementRef);

  constructor() {
    toObservable(this.valuesChanged)
      .pipe(takeUntilDestroyed())
      .subscribe(changes => {
        // the inject value is undefined, do nothing
        if (
          this.injectAttribute() === undefined ||
          this.injectAttribute().length === 0
        ) {
          return;
        }

        changes.forEach((changes, i) => {
          // Is the previous value undefined?
          // Has the CCS path or the attribute changed?
          // If so, remove old attribute from element, clean up.
          if (
            this.previousValue()[i] !== undefined &&
            (changes.CSSPath || changes.attribute)
          ) {
            const element = this.el.nativeElement.querySelector(
              this.previousValue()[i].CSSPath
            )!;

            // This should always be true, but maybe there was an error in our code,
            // better to put a safe guard
            if (element.hasAttribute(this.previousValue()[i].attribute)) {
              element.removeAttribute(this.previousValue()[i].attribute);
            }
          }

          // Finally create/update the attribute
          let elements = this.el.nativeElement.querySelectorAll(
            this.injectAttribute()[i].CSSPath
          )!;

          const setAttributes = () => {
            elements.forEach(element => {
              element.setAttribute(
                this.injectAttribute()[i].attribute,
                this.injectAttribute()[i].value
              );
            });
          };

          if (elements.length === 0) {
            // Wait 1000ms for elements to become visible
            setTimeout(() => {
              elements = this.el.nativeElement.querySelectorAll(
                this.injectAttribute()[i].CSSPath
              )!;

              setAttributes();
            }, 1000);
          } else {
            setAttributes();
          }
        });

        this.previousValue.set(this.injectAttribute());
      });
  }
}
